
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import AddIcon from '@mui/icons-material/Add';
import Dropzone from 'react-dropzone';
import toast from 'react-hot-toast';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getEndpoint, getApiUrl } from '../config';

function PageEditor() {
  const { section } = useParams();
  const navigate = useNavigate();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dragRowIdx, setDragRowIdx] = useState(null);
  const [dragCol, setDragCol] = useState({ rowIdx: null, colIdx: null });
  const [editItem, setEditItem] = useState(null); // { rowIdx, colIdx, type, value, ... }
  const [imageLink, setImageLink] = useState('');
  const [addRowOpen, setAddRowOpen] = useState(false);
  const [addColRowIdx, setAddColRowIdx] = useState(null);
  const [newColType, setNewColType] = useState('text');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [rowSettingsIdx, setRowSettingsIdx] = useState(null);
  const [rowSettings, setRowSettings] = useState({ columns: 1, bgColor: '#fff', padding: 16 });
  const [colSettings, setColSettings] = useState({ bgColor: '#fff', padding: 8 });
  const [colSettingsIdx, setColSettingsIdx] = useState({ rowIdx: null, colIdx: null });
  const [imageUploadFile, setImageUploadFile] = useState(null);
  const [imageUploading, setImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState('');
  const [imageBlobs, setImageBlobs] = useState({});
  const [addRowDialogOpen, setAddRowDialogOpen] = useState(false);
  const [newRowColumns, setNewRowColumns] = useState(1);
  const [pageId, setPageId] = useState(null);

  useEffect(() => {
    const fetchPage = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        // آدرس صحیح برای صفحات اختصاصی
        const res = await fetch(`${getEndpoint('CUSTOM_PAGES')}/section/${section}`, {
          headers: { 'Authorization': 'Bearer ' + token }
        });
        if (res.status === 404) {
          setPage({ section, meta_title: '', meta_description: '', meta_keywords: '', content: [] });
          setPageId(null);
        } else {
          const data = await res.json();
          let parsedContent = data.content;
          if (typeof parsedContent === 'string') {
            try {
              parsedContent = JSON.parse(parsedContent);
            } catch {
              parsedContent = [];
            }
          }
          setPage({ ...data, content: Array.isArray(parsedContent) ? parsedContent : [] });
          setPageId(data.id);
        }
      } catch {
        setPage({ section, meta_title: '', meta_description: '', meta_keywords: '', content: [] });
        setPageId(null);
      }
      setLoading(false);
    };
    fetchPage();
  }, [section]);

  useEffect(() => {
    // دانلود تصاویر آیتم‌های تصویری از سرور
    (page?.content || []).forEach((row, rowIdx) => {
      (row.columns || []).forEach((item, colIdx) => {
        if (item.type === 'image' && item.value && !imageBlobs[`${rowIdx}-${colIdx}`]) {
          const fileName = item.value.split('/').pop();
          fetch(`/api/media/download?path=${fileName}`, {
            headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') }
          })
            .then(res => res.blob())
            .then(blob => {
              const url = URL.createObjectURL(blob);
              setImageBlobs(urls => ({ ...urls, [`${rowIdx}-${colIdx}`]: url }));
            });
        }
      });
    });
    // پاکسازی object URLs
    return () => {
      Object.values(imageBlobs).forEach(url => URL.revokeObjectURL(url));
    };
  }, [page]);

  // Drag & Drop ردیف‌ها
  const handleRowDragStart = (idx) => setDragRowIdx(idx);
  const handleRowDragOver = (idx) => {
    if (dragRowIdx === null || dragRowIdx === idx) return;
    setPage(prev => {
      const newContent = [...(prev.content || [])];
      const [dragged] = newContent.splice(dragRowIdx, 1);
      newContent.splice(idx, 0, dragged);
      return { ...prev, content: newContent };
    });
    setDragRowIdx(idx);
  };
  const handleRowDragEnd = () => setDragRowIdx(null);

  // Drag & Drop آیتم‌ها
  const handleColDragStart = (rowIdx, colIdx) => setDragCol({ rowIdx, colIdx });
  const handleColDragOver = (rowIdx, colIdx) => {
    if (dragCol.rowIdx === null || dragCol.colIdx === null) return;
    if (dragCol.rowIdx === rowIdx && dragCol.colIdx === colIdx) return;
    setPage(prev => {
      const newContent = [...(prev.content || [])];
      const row = newContent[rowIdx];
      const [dragged] = row.columns.splice(dragCol.colIdx, 1);
      row.columns.splice(colIdx, 0, dragged);
      newContent[rowIdx] = row;
      return { ...prev, content: newContent };
    });
    setDragCol({ rowIdx, colIdx });
  };
  const handleColDragEnd = () => setDragCol({ rowIdx: null, colIdx: null });

  const handleSave = async () => {
    const sectionValue = page.section || section;
    const metaTitleValue = page.meta_title || 'بدون عنوان';
    if (!sectionValue || !metaTitleValue) {
      toast.error('عنوان و آدرس صفحه الزامی است.');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      const method = page.id ? 'PUT' : 'POST';
      const url = page.id ? `${getEndpoint('CUSTOM_PAGES')}/${page.id}` : getEndpoint('CUSTOM_PAGES');
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token
        },
        body: JSON.stringify({
          section: sectionValue,
          meta_title: metaTitleValue,
          meta_description: page.meta_description,
          meta_keywords: page.meta_keywords,
          content: JSON.stringify(page.content)
        })
      });
      if (res.ok) {
        toast.success('تغییرات با موفقیت ذخیره شد.');
      } else {
        toast.error('خطا در ذخیره‌سازی!');
      }
    } catch {
      toast.error('خطا در ذخیره‌سازی!');
    }
  };

  if (loading) return <Typography sx={{ fontFamily: 'Vazir', mt: 4 }}>در حال بارگذاری...</Typography>;
  if (!page) return <Typography sx={{ fontFamily: 'Vazir', mt: 4 }}>صفحه پیدا نشد.</Typography>;

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', mt: 4, direction: 'rtl', position: 'relative' }}>
      <Box sx={{ position: 'fixed', top: 88, left: 24, zIndex: 1201, direction: 'ltr' }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate(-1)} sx={{ fontFamily: 'Vazir', fontWeight: 'bold', bgcolor: '#fff', boxShadow: 2, borderRadius: 2 }}>
          بازگشت
        </Button>
      </Box>
      <Box sx={{ pt: 8, maxWidth: previewMode === 'desktop' ? 1280 : undefined, mx: 'auto' }}>
        <Typography variant="h5" sx={{ fontFamily: 'Vazir', fontWeight: 'bold', mb: 3, textAlign: 'right' }}>{page.meta_title || page.section}</Typography>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2, direction: 'rtl' }}>
          <Button variant={previewMode === 'desktop' ? 'contained' : 'outlined'} onClick={() => setPreviewMode('desktop')} sx={{ fontFamily: 'Vazir' }}>دسکتاپ</Button>
          <Button variant={previewMode === 'tablet' ? 'contained' : 'outlined'} onClick={() => setPreviewMode('tablet')} sx={{ fontFamily: 'Vazir' }}>تبلت</Button>
          <Button variant={previewMode === 'mobile' ? 'contained' : 'outlined'} onClick={() => setPreviewMode('mobile')} sx={{ fontFamily: 'Vazir' }}>موبایل</Button>
          <Divider flexItem orientation="vertical" />
          <Button variant="contained" color="success" sx={{ fontFamily: 'Vazir' }} onClick={handleSave}>ذخیره تغییرات</Button>
        </Stack>
        {/* Page Builder Editor Start */}
        {/* افزودن دکمه افزودن ردیف در بالا فقط اگر هیچ ردیفی وجود ندارد */}
        {(page.content === null || page.content.length === 0) && (
          <Box sx={{ direction: 'rtl', mb: 2, display: 'flex', justifyContent: 'center' }}>
            <Button variant="contained" color="primary" sx={{ fontFamily: 'Vazir', borderRadius: 8, minWidth: 44, minHeight: 44, p: 0 }} onClick={() => setAddRowDialogOpen(true)}>
              <AddIcon />
            </Button>
          </Box>
        )}
        <Box sx={{ direction: 'rtl' }}>
          {(page.content || []).map((row, rowIdx) => (
            <React.Fragment key={rowIdx}>
              <Box
                sx={{ border: '1px solid #eee', borderRadius: 2, mb: 2, p: row.padding || 16, position: 'relative', background: row.bgColor || '#fff', direction: 'rtl' }}
                draggable
                onDragStart={() => handleRowDragStart(rowIdx)}
                onDragOver={e => { e.preventDefault(); handleRowDragOver(rowIdx); }}
                onDragEnd={handleRowDragEnd}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ position: 'absolute', top: 8, right: 8 }}>
                  <IconButton size="small" onClick={() => setRowSettingsIdx(rowIdx)} title="تنظیمات ردیف">
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton size="small" color="error" onClick={() => {
                    setPage(prev => {
                      const newContent = [...(prev.content || [])];
                      newContent.splice(rowIdx, 1);
                      return { ...prev, content: newContent };
                    });
                  }}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Typography sx={{ fontFamily: 'Vazir', fontWeight: 'bold', mb: 1, pr: 6 }}>ردیف {rowIdx + 1}</Typography>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', flexDirection: previewMode === 'mobile' ? 'column' : previewMode === 'tablet' ? 'row' : 'row' }}>
                  {(row.columns || []).map((item, colIdx) => (
                    <Box
                      key={colIdx}
                      sx={{ border: '1px solid #ccc', borderRadius: 1, p: item.padding || 8, minWidth: previewMode === 'desktop' ? 160 : previewMode === 'tablet' ? 120 : '100%', flex: previewMode === 'desktop' ? `1 1 ${100/(row.columnsCount||row.columns||1)}%` : previewMode === 'tablet' ? '1 1 45%' : '1 1 100%', position: 'relative', background: item.bgColor || '#fff' }}
                      draggable
                      onDragStart={() => handleColDragStart(rowIdx, colIdx)}
                      onDragOver={e => { e.preventDefault(); handleColDragOver(rowIdx, colIdx); }}
                      onDragEnd={handleColDragEnd}
                    >
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ position: 'absolute', top: 2, left: 2 }}>
                        <IconButton size="small" onClick={() => setColSettingsIdx({ rowIdx, colIdx })} title="تنظیمات آیتم">
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          // مقداردهی پیش‌فرض برای type و value و link
                          setEditItem({
                            rowIdx,
                            colIdx,
                            type: item.type || 'text',
                            value: item.value || '',
                            link: item.link || ''
                          });
                        }}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => {
                          setPage(prev => {
                            const newContent = [...(prev.content || [])];
                            newContent[rowIdx].columns = newContent[rowIdx].columns.filter((_, i) => i !== colIdx);
                            return { ...prev, content: newContent };
                          });
                        }}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Stack>
                      <Typography sx={{ fontFamily: 'Vazir', fontSize: 13 }}>آیتم {colIdx + 1}</Typography>
                      {/* نمایش محتوای آیتم */}
                      {item.type === 'text' && (
                        <Typography sx={{ fontFamily: 'Vazir', fontSize: 14, mt: 1, mb: 1 }}>
                          {item.value || 'متن نمونه...'}
                        </Typography>
                      )}
                      {item.type === 'image' && (
                        <Box sx={{ mt: 1, mb: 1 }}>
                          {item.link ? (
                            <a href={item.link} target="_blank" rel="noopener noreferrer">
                              <img src={imageBlobs[`${rowIdx}-${colIdx}`] || item.value || 'https://via.placeholder.com/100x60?text=تصویر'} alt="img" style={{ maxWidth: '100%', maxHeight: 60, borderRadius: 4 }} />
                            </a>
                          ) : (
                            <img src={imageBlobs[`${rowIdx}-${colIdx}`] || item.value || 'https://via.placeholder.com/100x60?text=تصویر'} alt="img" style={{ maxWidth: '100%', maxHeight: 60, borderRadius: 4 }} />
                          )}
                        </Box>
                      )}
                    </Box>
                  ))}
                  {/* دکمه‌های افزودن آیتم به صورت مربعی با رنگ خاکستری و آیکون نقطه‌ای */}
                  {Array.from({ length: (row.columnsCount || row.columns || 1) - (row.columns?.length || 0) }).map((_, i) => (
                    <Button key={i}
                      variant="outlined"
                      sx={{
                        fontFamily: 'Vazir',
                        height: 44,
                        minWidth: 44,
                        borderRadius: 2,
                        p: 0,
                        border: 'none',
                        bgcolor: 'grey.100',
                        color: 'grey.400',
                        boxShadow: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        '&:hover': { bgcolor: 'grey.200', color: 'grey.600' }
                      }}
                      onClick={() => setAddColRowIdx(rowIdx)}
                    >
                      <AddIcon sx={{ fontSize: 28, color: 'grey.400' }} />
                    </Button>
                  ))}
                </Box>
              </Box>
              {/* دکمه افزودن ردیف بعد از هر ردیف */}
              <Box sx={{ direction: 'rtl', mb: 2, display: 'flex', justifyContent: 'center' }}>
                <Button variant="contained" color="primary" sx={{ fontFamily: 'Vazir', borderRadius: 8, minWidth: 44, minHeight: 44, p: 0 }} onClick={() => setAddRowDialogOpen(true)}>
                  <AddIcon />
                </Button>
              </Box>
            </React.Fragment>
          ))}
         
        </Box>
        {/* Page Builder Editor End */}
        {/* Dialog تنظیمات ردیف */}
        <Dialog open={rowSettingsIdx !== null} onClose={() => setRowSettingsIdx(null)} PaperProps={{ sx: { direction: 'rtl', textAlign: 'right', fontFamily: 'Vazir' } }}>
          <DialogTitle sx={{ fontFamily: 'Vazir' }}>تنظیمات ردیف</DialogTitle>
          <DialogContent>
            <Select
              value={rowSettings.columns}
              onChange={e => setRowSettings(s => ({ ...s, columns: Number(e.target.value) }))}
              sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 120 }}
            >
              {[1,2,3,4].map(n => <MenuItem key={n} value={n} sx={{ fontFamily: 'Vazir' }}>{n} ستون</MenuItem>)}
            </Select>
            <TextField
              label="رنگ پس‌زمینه"
              type="color"
              value={rowSettings.bgColor}
              onChange={e => setRowSettings(s => ({ ...s, bgColor: e.target.value }))}
              sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 120 }}
            />
            <TextField
              label="Padding (پدینگ)"
              type="number"
              value={rowSettings.padding}
              onChange={e => setRowSettings(s => ({ ...s, padding: Number(e.target.value) }))}
              sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 120 }}
            />
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', justifyContent: 'flex-start', px: 3 }}>
            <Button onClick={() => setRowSettingsIdx(null)} color="secondary" sx={{ fontFamily: 'Vazir', ml: 2 }}>انصراف</Button>
            <Button onClick={() => {
              setPage(prev => {
                const newContent = [...(prev.content || [])];
                newContent[rowSettingsIdx] = {
                  ...newContent[rowSettingsIdx],
                  columnsCount: rowSettings.columns,
                  bgColor: rowSettings.bgColor,
                  padding: rowSettings.padding
                };
                return { ...prev, content: newContent };
              });
              setRowSettingsIdx(null);
            }} color="primary" variant="contained" sx={{ fontFamily: 'Vazir' }}>ذخیره</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog تنظیمات آیتم */}
        <Dialog open={colSettingsIdx.rowIdx !== null} onClose={() => setColSettingsIdx({ rowIdx: null, colIdx: null })} PaperProps={{ sx: { direction: 'rtl', textAlign: 'right', fontFamily: 'Vazir' } }}>
          <DialogTitle sx={{ fontFamily: 'Vazir' }}>تنظیمات آیتم</DialogTitle>
          <DialogContent>
            <TextField
              label="رنگ پس‌زمینه"
              type="color"
              value={colSettings.bgColor}
              onChange={e => setColSettings(s => ({ ...s, bgColor: e.target.value }))}
              sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 120 }}
            />
            <TextField
              label="Padding (پدینگ)"
              type="number"
              value={colSettings.padding}
              onChange={e => setColSettings(s => ({ ...s, padding: Number(e.target.value) }))}
              sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 120 }}
            />
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', justifyContent: 'flex-start', px: 3 }}>
            <Button onClick={() => setColSettingsIdx({ rowIdx: null, colIdx: null })} color="secondary" sx={{ fontFamily: 'Vazir', ml: 2 }}>انصراف</Button>
            <Button onClick={() => {
              setPage(prev => {
                const newContent = [...(prev.content || [])];
                const row = newContent[colSettingsIdx.rowIdx];
                row.columns[colSettingsIdx.colIdx] = {
                  ...row.columns[colSettingsIdx.colIdx],
                  bgColor: colSettings.bgColor,
                  padding: colSettings.padding
                };
                newContent[colSettingsIdx.rowIdx] = row;
                return { ...prev, content: newContent };
              });
              setColSettingsIdx({ rowIdx: null, colIdx: null });
            }} color="primary" variant="contained" sx={{ fontFamily: 'Vazir' }}>ذخیره</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog ویرایش آیتم */}
        <Dialog open={!!editItem} onClose={() => setEditItem(null)} maxWidth="md" fullWidth PaperProps={{ sx: { direction: 'rtl', textAlign: 'right', fontFamily: 'Vazir' } }}>
          <DialogTitle sx={{ fontFamily: 'Vazir' }}>ویرایش آیتم</DialogTitle>
          <DialogContent>
            {editItem && (
              <>
                {/* انتخاب نوع آیتم */}
                <Typography sx={{ fontFamily: 'Vazir', mb: 2, color: 'text.secondary' }}>
                  نوع آیتم:
                </Typography>
                <Select
                  value={editItem.type}
                  onChange={e => {
                    const newType = e.target.value;
                    setEditItem({
                      ...editItem,
                      type: newType,
                      value: '',
                      link: ''
                    });
                  }}
                  sx={{ fontFamily: 'Vazir', mb: 2, minWidth: 180 }}
                >
                  <MenuItem value="text" sx={{ fontFamily: 'Vazir' }}>متن</MenuItem>
                  <MenuItem value="image" sx={{ fontFamily: 'Vazir' }}>تصویر</MenuItem>
                </Select>
                {editItem.type === 'text' && (
                  <Box sx={{ mt: 2 }}>
                    <ReactQuill
                      value={editItem.value || ''}
                      onChange={val => setEditItem({ ...editItem, value: val })}
                      style={{ minHeight: 180, fontFamily: 'Vazir' }}
                      theme="snow"
                    />
                  </Box>
                )}
                {editItem.type === 'image' && (
                  <>
                    <Dropzone
                      onDrop={async (acceptedFiles) => {
                        const file = acceptedFiles[0];
                        if (!file) return;
                        if (file.size > 500 * 1024) {
                          setImageUploadError('حجم عکس نباید بیشتر از ۵۰۰ کیلوبایت باشد.');
                          return;
                        }
                        setImageUploadError('');
                        setImageUploading(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          formData.append('type', 'image');
                          const res = await fetch(getEndpoint('MEDIA'), {
                            method: 'POST',
                            headers: { 'Authorization': 'Bearer ' + localStorage.getItem('token') },
                            body: formData
                          });
                          if (res.ok) {
                            const data = await res.json();
                            console.log('uploaded image url:', data.url); // لاگ مقدار برگشتی
                            setEditItem({ ...editItem, value: data.url });
                            toast.success('تصویر با موفقیت آپلود شد');
                          } else {
                            setImageUploadError('خطا در آپلود عکس');
                          }
                        } catch (error) {
                          setImageUploadError('خطا در آپلود عکس');
                        } finally {
                          setImageUploading(false);
                        }
                      }}
                      maxFiles={1}
                      accept={{ 'image/*': [] }}
                      maxSize={500 * 1024}
                    >
                      {({ getRootProps, getInputProps, isDragActive }) => (
                        <Box {...getRootProps()} sx={{ 
                          border: '2px dashed #90caf9', 
                          borderRadius: 2, 
                          p: 2, 
                          textAlign: 'center', 
                          bgcolor: isDragActive ? '#e3f2fd' : '#fafafa', 
                          cursor: 'pointer', 
                          fontFamily: 'Vazir', 
                          mb: 2,
                          minHeight: 100,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <input {...getInputProps()} />
                          {imageUploading ? (
                            <Typography sx={{ fontFamily: 'Vazir' }}>در حال آپلود...</Typography>
                          ) : (
                            <Typography sx={{ fontFamily: 'Vazir' }}>
                              {isDragActive ? 'فایل را اینجا رها کنید' : 'یک عکس انتخاب یا اینجا رها کنید (حداکثر ۵۰۰ کیلوبایت)'}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Dropzone>
                    {imageUploadError && (
                      <Typography color="error" sx={{ fontFamily: 'Vazir', fontSize: 13, mb: 2 }}>
                        {imageUploadError}
                      </Typography>
                    )}
                    <TextField
                      label="آدرس تصویر (URL)"
                      value={editItem.value || ''}
                      onChange={e => setEditItem({ ...editItem, value: e.target.value })}
                      sx={{ fontFamily: 'Vazir', mt: 1, minWidth: 250 }}
                      fullWidth
                    />
                    <TextField
                      label="لینک (اختیاری)"
                      value={editItem.link || ''}
                      onChange={e => setEditItem({ ...editItem, link: e.target.value })}
                      sx={{ fontFamily: 'Vazir', mt: 2, minWidth: 250 }}
                      fullWidth
                    />
                    <Box sx={{ mt: 2, textAlign: 'center' }}>
                      <img 
                        src={
                          editItem.value
                            ? editItem.value.startsWith('http')
                              ? editItem.value
                              : `${getApiUrl('')}${editItem.value}`
                            : 'https://via.placeholder.com/180x120?text=تصویر'
                        }
                        alt="preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: 180, 
                          borderRadius: 8,
                          border: '1px solid #ddd'
                        }} 
                      />
                    </Box>
                  </>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', justifyContent: 'flex-start', px: 3 }}>
            <Button onClick={() => setEditItem(null)} sx={{ fontFamily: 'Vazir', ml: 2 }}>انصراف</Button>
            <Button onClick={() => {
              if (editItem) {
                setPage(prev => {
                  const newContent = [...(prev.content || [])];
                  const row = { ...newContent[editItem.rowIdx] }; // کپی ردیف
                  const columns = [...(row.columns || [])]; // کپی آرایه ستون‌ها
                  columns[editItem.colIdx] = {
                    ...columns[editItem.colIdx],
                    type: editItem.type,
                    value: editItem.value,
                    link: editItem.link
                  };
                  row.columns = columns;
                  newContent[editItem.rowIdx] = row;
                  return { ...prev, content: newContent };
                });
                setEditItem(null);
                toast.success('تغییرات ذخیره شد');
              }
            }} variant="contained" color="primary" sx={{ fontFamily: 'Vazir' }}>ذخیره</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog افزودن آیتم جدید */}
        <Dialog open={addColRowIdx !== null} onClose={() => setAddColRowIdx(null)} PaperProps={{ sx: { direction: 'rtl', textAlign: 'right', fontFamily: 'Vazir' } }}>
          <DialogTitle sx={{ fontFamily: 'Vazir' }}>انتخاب نوع آیتم</DialogTitle>
          <DialogContent>
            <Select
              value={newColType}
              onChange={e => setNewColType(e.target.value)}
              sx={{ fontFamily: 'Vazir', minWidth: 180 }}
            >
              <MenuItem value="text" sx={{ fontFamily: 'Vazir' }}>متن</MenuItem>
              <MenuItem value="image" sx={{ fontFamily: 'Vazir' }}>تصویر</MenuItem>
            </Select>
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', justifyContent: 'flex-start', px: 3 }}>
            <Button onClick={() => setAddColRowIdx(null)} sx={{ fontFamily: 'Vazir', ml: 2 }}>انصراف</Button>
            <Button onClick={() => {
              setPage(prev => {
                const newContent = [...(prev.content || [])];
                const row = newContent[addColRowIdx];
                row.columns = [
                  ...(row.columns || []),
                  { type: newColType, value: '', bgColor: '#fff', padding: 8 }
                ];
                newContent[addColRowIdx] = row;
                return { ...prev, content: newContent };
              });
              setAddColRowIdx(null);
            }} variant="contained" color="primary" sx={{ fontFamily: 'Vazir' }}>افزودن</Button>
          </DialogActions>
        </Dialog>
        {/* Dialog انتخاب تعداد ستون برای ردیف جدید */}
        <Dialog open={addRowDialogOpen} onClose={() => setAddRowDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontFamily: 'Vazir', fontWeight: 'bold', textAlign: 'right' }}>تعداد ستون ردیف جدید</DialogTitle>
          <DialogContent sx={{ direction: 'rtl', textAlign: 'right' }}>
            <Typography sx={{ fontFamily: 'Vazir', mb: 2 }}>تعداد ستون (۱ تا ۴):</Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', mb: 2 }}>
              {[1,2,3,4].map(n => (
                <Button
                  key={n}
                  variant="outlined"
                  sx={{
                    minWidth: 56,
                    minHeight: 56,
                    borderRadius: 2,
                    fontFamily: 'Vazir',
                    fontSize: 22,
                    fontWeight: 'bold',
                    bgcolor: '#fff',
                    border: '2px solid',
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    boxShadow: 1,
                    transition: 'all 0.2s',
                    '&:hover': { bgcolor: '#1976d2', color: '#fff' }
                  }}
                  onClick={() => {
                    setPage(prev => ({
                      ...prev,
                      content: [
                        ...(prev.content || []),
                        { columns: Array.from({ length: n }, () => ({ type: 'text', value: '', bgColor: '#fff', padding: 8 })), columnsCount: n, bgColor: '#fff', padding: 16 }
                      ]
                    }));
                    setAddRowDialogOpen(false);
                    setNewRowColumns(1);
                  }}
                >
                  {n}
                </Button>
              ))}
            </Box>
          </DialogContent>
          <DialogActions sx={{ direction: 'rtl', justifyContent: 'flex-start', px: 3 }}>
            <Button onClick={() => setAddRowDialogOpen(false)} color="secondary" variant="outlined" sx={{ fontFamily: 'Vazir', minWidth: 100, ml: 2 }}>انصراف</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Box>
  );
}

export default PageEditor; 